<template>
    <nav class="flex items-center gap-1 overflow-x-auto min-w-0 -mb-px">
        <button
            type="button"
            class="whitespace-nowrap rounded-t-md border-b-2 px-3 pb-2 pt-1.5 text-sm font-medium transition-colors"
            :class="activeId === ALL ? 'border-primary-500 bg-gray-100 text-gray-900' : 'border-transparent text-gray-500 hover:bg-gray-50 hover:text-gray-700'"
            @click="select(ALL)"
        >
            All
        </button>

        <div
            v-for="(v, i) in views"
            :key="v.id"
            class="group flex items-center whitespace-nowrap rounded-t-md border-b-2 transition-colors"
            :class="activeId === v.id ? 'border-primary-500 bg-gray-100' : 'border-transparent hover:bg-gray-50'"
        >
            <button
                type="button"
                class="pb-2 pl-3 pt-1.5 text-sm font-medium transition-colors"
                :class="[activeId === v.id ? 'text-gray-900' : 'text-gray-500 group-hover:text-gray-700', (canManage && activeId === v.id) ? 'pr-1' : 'pr-3']"
                @click="select(v.id)"
            >
                {{ v.name }}
            </button>
            <UDropdown
                v-if="canManage && activeId === v.id"
                :items="menuItems(v, i)"
                :popper="{ placement: 'bottom-start' }"
                :ui="{ width: 'w-56' }"
                class="pr-1"
            >
                <UButton icon="i-ph-dots-three-vertical-bold" size="2xs" color="gray" variant="ghost" :padded="false" class="p-0.5" />
            </UDropdown>
        </div>

        <!-- Save button lives in the page's right controls -->
        <Teleport v-if="mounted && dirty" to="#view-save-slot">
            <UButton color="black" size="sm" :loading="saving" @click="onSaveClick">
                {{ activeId === ALL ? 'Save view' : 'Save changes' }}
            </UButton>
        </Teleport>
    </nav>

    <UModal v-model="nameModal.open">
        <UCard>
            <template #header><h3 class="text-lg font-semibold">{{ nameModal.title }}</h3></template>
            <UInput v-model="nameModal.value" placeholder="View name" autofocus @keyup.enter="nameModal.confirm()" />
            <template #footer>
                <div class="flex justify-end gap-2">
                    <UButton color="gray" variant="ghost" @click="nameModal.open = false">Cancel</UButton>
                    <UButton :loading="saving" :disabled="!nameModal.value.trim()" @click="nameModal.confirm()">Save</UButton>
                </div>
            </template>
        </UCard>
    </UModal>
</template>

<script setup lang="ts">
import type { SavedView } from '~/types/saved-view'
import { UserRole } from '~/types/user'

const props = defineProps<{ page: 'customers' | 'calls'; currentFilter: any; canManage?: boolean }>()
const emit = defineEmits<{ apply: [filters: any, initial?: boolean] }>()

const { list, create, update, remove } = useSavedViews()
const auth = useAuthStore()
const route = useRoute()
const router = useRouter()

const ALL = '__all__'
const views = ref<SavedView[]>([])
const activeId = ref<string>(ALL)
const mounted = ref(false)
const saving = ref(false)

const activeView = computed(() => views.value.find(v => v.id === activeId.value) ?? null)

const isEmpty = computed(() => {
    const f = props.currentFilter
    if (!f) return true
    if (Array.isArray(f)) return !f.some((c: any) => c?.value != null && (!Array.isArray(c.value) || c.value.length))
    return Object.keys(f).length === 0
})

const dirty = computed(() => {
    if (activeId.value === ALL) return !isEmpty.value
    if (!activeView.value) return false
    return JSON.stringify(props.currentFilter) !== JSON.stringify(activeView.value.filters)
})

async function refresh() { views.value = await list(props.page) }

onMounted(async () => {
    await refresh()
    const q = route.query.view as string | undefined
    if (q && views.value.some(v => v.id === q)) {
        activeId.value = q
        emit('apply', views.value.find(v => v.id === q)!.filters, true)
    }
    mounted.value = true
})

function setViewParam(id: string) {
    router.replace({ query: { ...route.query, view: id === ALL ? undefined : id } })
}

function select(id: string) {
    activeId.value = id
    emit('apply', id === ALL ? null : (views.value.find(v => v.id === id)?.filters ?? null))
    setViewParam(id)
}

defineExpose({ select })

// ---- Save ----
function onSaveClick() {
    if (activeId.value === ALL) {
        nameModal.value = { open: true, title: 'Save view', value: '', confirm: doCreate }
    } else {
        doSaveChanges()
    }
}

async function doCreate() {
    const name = nameModal.value.value.trim()
    if (!name) return
    saving.value = true
    try {
        const v = await create({ name, page: props.page, filters: props.currentFilter })
        await refresh()
        activeId.value = v.id
        setViewParam(v.id)
        nameModal.value.open = false
    } finally { saving.value = false }
}

async function doSaveChanges() {
    if (!activeView.value) return
    saving.value = true
    try {
        await update(activeView.value.id, { filters: props.currentFilter })
        await refresh()
    } finally { saving.value = false }
}

// ---- Per-view menu ----
function menuItems(v: SavedView, i: number) {
    const groups: any[] = [
        [
            { label: 'Move left', icon: 'i-ph-arrow-left', disabled: i === 0, click: () => move(i, -1) },
            { label: 'Move right', icon: 'i-ph-arrow-right', disabled: i === views.value.length - 1, click: () => move(i, 1) },
        ],
    ]
    if (props.page === 'customers' && auth.hasRole(UserRole.admin)) {
        groups.push([{ label: 'New Broadcast Segment', icon: 'i-ph-megaphone', click: () => newBroadcastSegment(v) }])
    }
    groups.push(
        [
            { label: 'Duplicate', icon: 'i-ph-copy', click: () => duplicate(v) },
            { label: 'Rename', icon: 'i-ph-pencil-simple', click: () => openRename(v) },
        ],
        [
            { label: 'Delete', icon: 'i-ph-trash', iconClass: 'text-red-400', click: () => onDelete(v) },
        ],
    )
    return groups
}

function newBroadcastSegment(v: SavedView) {
    navigateTo({ path: '/broadcasts/segments', query: { fromView: v.id } })
}

async function persistOrder(ordered: SavedView[]) {
    await Promise.all(ordered.map((v, idx) => (v.position === idx ? null : update(v.id, { position: idx }))).filter(Boolean) as Promise<void>[])
    views.value = ordered.map((v, idx) => ({ ...v, position: idx }))
}

async function move(i: number, dir: -1 | 1) {
    const j = i + dir
    if (j < 0 || j >= views.value.length) return
    const arr = [...views.value]
    ;[arr[i], arr[j]] = [arr[j], arr[i]]
    await persistOrder(arr)
}

async function duplicate(v: SavedView) {
    saving.value = true
    try {
        const created = await create({ name: `${v.name} copy`, page: props.page, filters: v.filters })
        const i = views.value.findIndex(x => x.id === v.id)
        const arr = [...views.value]
        arr.splice(i + 1, 0, created)
        await persistOrder(arr)
    } finally { saving.value = false }
}

function openRename(v: SavedView) {
    nameModal.value = {
        open: true,
        title: 'Rename view',
        value: v.name,
        confirm: async () => {
            const name = nameModal.value.value.trim()
            if (!name) return
            await update(v.id, { name })
            await refresh()
            nameModal.value.open = false
        },
    }
}

async function onDelete(v: SavedView) {
    if (!confirm(`Delete "${v.name}"?`)) return
    await remove(v.id)
    await refresh()
    if (activeId.value === v.id) select(ALL)
}

const nameModal = ref<{ open: boolean; title: string; value: string; confirm: () => void }>({
    open: false, title: '', value: '', confirm: () => {},
})
</script>
